import React, { useState, useEffect, useRef } from "react";
import { getFirestore, collection, getDocs, query, orderBy, onSnapshot, Unsubscribe } from "firebase/firestore";
import { useUserRole } from "../hooks/useUserRole";
import "../styles/ClubAccounting.css";

interface Transaction {
  txId: string;
  userId: string;
  type: string;
  amount: number;
  reason?: string;
  status: string;
  createdAt: Date;
  walletItemId?: string;
  category?: string;
}

interface UserBalance {
  userId: string;
  email: string;
  displayName: string;
  balance: number;
  transactionCount: number;
}

interface CategoryTotal {
  category: string;
  total: number;
  count: number;
}

interface FilterState {
  startDate: string;
  endDate: string;
  userSearch: string;
  selectedCategory: string;
  selectedStatus: string;
}

export function ClubAccounting() {
  const { role } = useUserRole();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [userBalances, setUserBalances] = useState<UserBalance[]>([]);
  const [categoryTotals, setCategoryTotals] = useState<CategoryTotal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "transactions" | "users" | "categories">(
    "overview"
  );
  const [filters, setFilters] = useState<FilterState>({
    startDate: "",
    endDate: "",
    userSearch: "",
    selectedCategory: "ALL",
    selectedStatus: "ALL",
  });
  const [liveUpdates, setLiveUpdates] = useState(true);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  const db = getFirestore();

  useEffect(() => {
    loadAccountingDataWithListener();
    return () => {
      // Cleanup listener on unmount
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const loadAccountingDataWithListener = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cleanup old listener if exists
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      // Setup real-time listener if enabled
      if (liveUpdates) {
        const txCollection = collection(db, "ledger_transactions");
        const txQuery = query(txCollection, orderBy("createdAt", "desc"));

        unsubscribeRef.current = onSnapshot(
          txQuery,
          (snapshot) => {
            processTransactionData(snapshot.docs);
          },
          (err) => {
            console.error("Error listening to transactions:", err);
            setError("Error loading real-time data");
          }
        );
      } else {
        // Fallback to one-time fetch
        loadAccountingData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load accounting data");
      console.error("Error loading accounting data:", err);
      setLoading(false);
    }
  };

  const processTransactionData = async (txDocs: any[]) => {
    try {
      const txList: Transaction[] = txDocs.map((doc) => {
        const data = doc.data();
        return {
          txId: data.txId || doc.id,
          userId: data.userId,
          type: data.type,
          amount: data.amount || 0,
          reason: data.reason,
          status: data.status,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          walletItemId: data.walletItemId,
          category: categorizeTransaction(data.type),
        };
      });

      // Calculate user balances
      const userMap = new Map<string, UserBalance>();
      const usersCollection = collection(db, "users");
      const usersSnapshot = await getDocs(usersCollection);

      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        userMap.set(data.userId, {
          userId: data.userId,
          email: data.email,
          displayName: data.displayName,
          balance: 0,
          transactionCount: 0,
        });
      });

      // Calculate balances from transactions
      txList.forEach((tx) => {
        const user = userMap.get(tx.userId);
        if (user) {
          if (tx.type === "ISSUE" || tx.type === "CREDIT") {
            user.balance += tx.amount;
          } else if (tx.type === "REDEEM" || tx.type === "DEBIT") {
            user.balance -= tx.amount;
          }
          user.transactionCount++;
        }
      });

      const userBalancesList = Array.from(userMap.values())
        .filter((u) => u.transactionCount > 0)
        .sort((a, b) => b.balance - a.balance);

      setUserBalances(userBalancesList);

      // Calculate category totals
      const categoryMap = new Map<string, CategoryTotal>();
      txList.forEach((tx) => {
        const cat = tx.category || "OTHER";
        const existing = categoryMap.get(cat);
        if (existing) {
          existing.total += tx.amount;
          existing.count++;
        } else {
          categoryMap.set(cat, {
            category: cat,
            total: tx.amount,
            count: 1,
          });
        }
      });

      const categoryList = Array.from(categoryMap.values()).sort((a, b) => b.total - a.total);
      setCategoryTotals(categoryList);

      setTransactions(txList);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to process data");
      console.error("Error processing data:", err);
      setLoading(false);
    }
  };

  const loadAccountingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load transactions (one-time)
      const txCollection = collection(db, "ledger_transactions");
      const txQuery = query(txCollection, orderBy("createdAt", "desc"));
      const txSnapshot = await getDocs(txQuery);

      const txDocs = txSnapshot.docs;
      await processTransactionData(txDocs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load accounting data");
      console.error("Error loading accounting data:", err);
      setLoading(false);
    }
  };

  const categorizeTransaction = (type: string): string => {
    switch (type) {
      case "ISSUE":
        return "Gift Cards Issued";
      case "REDEEM":
        return "Redemptions";
      case "CREDIT":
        return "Credits";
      case "DEBIT":
        return "Debits";
      case "CLAIM":
        return "Claims";
      case "EXPIRE":
        return "Expired";
      default:
        return "Other";
    }
  };

  const getFilteredTransactions = (): Transaction[] => {
    return transactions.filter((tx) => {
      // Date filter
      if (filters.startDate) {
        const txDate = new Date(tx.createdAt);
        const startDate = new Date(filters.startDate);
        if (txDate < startDate) return false;
      }
      if (filters.endDate) {
        const txDate = new Date(tx.createdAt);
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        if (txDate > endDate) return false;
      }

      // User search filter
      if (filters.userSearch.trim()) {
        const search = filters.userSearch.toLowerCase();
        const user = userBalances.find((u) => u.userId === tx.userId);
        if (!user) return false;
        if (
          !user.email.toLowerCase().includes(search) &&
          !user.displayName.toLowerCase().includes(search) &&
          !tx.userId.toLowerCase().includes(search)
        ) {
          return false;
        }
      }

      // Category filter
      if (filters.selectedCategory !== "ALL" && tx.category !== filters.selectedCategory) {
        return false;
      }

      // Status filter
      if (filters.selectedStatus !== "ALL" && tx.status !== filters.selectedStatus) {
        return false;
      }

      return true;
    });
  };

  const getFilteredUsers = (): UserBalance[] => {
    return userBalances.filter((user) => {
      if (filters.userSearch.trim()) {
        const search = filters.userSearch.toLowerCase();
        return (
          user.email.toLowerCase().includes(search) ||
          user.displayName.toLowerCase().includes(search) ||
          user.userId.toLowerCase().includes(search)
        );
      }
      return true;
    });
  };

  const getUniqueCategories = (): string[] => {
    const categories = new Set(transactions.map((tx) => tx.category || "OTHER"));
    return Array.from(categories).sort();
  };

  const getUniqueStatuses = (): string[] => {
    const statuses = new Set(transactions.map((tx) => tx.status));
    return Array.from(statuses).sort();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
    }).format(amount);
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat("no-NO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (role !== "CLUB") {
    return (
      <div className="club-accounting">
        <h1>Access Denied</h1>
        <p>You need CLUB admin privileges to access this page.</p>
      </div>
    );
  }

  const filteredTransactions = getFilteredTransactions();
  const filteredUsers = getFilteredUsers();
  const totalAmount = filteredTransactions.reduce((sum, tx) => sum + tx.amount, 0);
  const totalTransactions = filteredTransactions.length;
  const totalUsers = filteredUsers.length;

  return (
    <div className="club-accounting">
      <h1>💰 Club Accounting & Finance</h1>

      {error && <div className="error-message">{error}</div>}

      {/* Overview Cards */}
      {activeTab === "overview" && (
        <div className="overview-cards">
          <div className="card">
            <div className="card-label">Total Transactions</div>
            <div className="card-value">{totalTransactions}</div>
          </div>
          <div className="card">
            <div className="card-label">Total Amount</div>
            <div className="card-value">{formatCurrency(totalAmount)}</div>
          </div>
          <div className="card">
            <div className="card-label">Active Users</div>
            <div className="card-value">{totalUsers}</div>
          </div>
          <div className="card">
            <div className="card-label">Avg Per User</div>
            <div className="card-value">
              {formatCurrency(totalUsers > 0 ? totalAmount / totalUsers : 0)}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          📊 Overview
        </button>
        <button
          className={`tab ${activeTab === "transactions" ? "active" : ""}`}
          onClick={() => setActiveTab("transactions")}
        >
          📝 All Transactions
        </button>
        <button
          className={`tab ${activeTab === "users" ? "active" : ""}`}
          onClick={() => setActiveTab("users")}
        >
          👥 User Balances
        </button>
        <button
          className={`tab ${activeTab === "categories" ? "active" : ""}`}
          onClick={() => setActiveTab("categories")}
        >
          📂 By Category
        </button>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filter-group">
          <label>
            🔄 Live Updates:
            <input
              type="checkbox"
              checked={liveUpdates}
              onChange={(e) => {
                setLiveUpdates(e.target.checked);
                if (e.target.checked) {
                  loadAccountingDataWithListener();
                } else if (unsubscribeRef.current) {
                  unsubscribeRef.current();
                }
              }}
            />
          </label>
        </div>

        <div className="filter-group">
          <label>
            📅 Start Date:
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </label>
        </div>

        <div className="filter-group">
          <label>
            📅 End Date:
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </label>
        </div>

        <div className="filter-group">
          <label>
            🔍 Search User:
            <input
              type="text"
              placeholder="Name, email, or ID..."
              value={filters.userSearch}
              onChange={(e) => setFilters({ ...filters, userSearch: e.target.value })}
            />
          </label>
        </div>

        <div className="filter-group">
          <label>
            📂 Category:
            <select
              value={filters.selectedCategory}
              onChange={(e) => setFilters({ ...filters, selectedCategory: e.target.value })}
            >
              <option value="ALL">All Categories</option>
              {getUniqueCategories().map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="filter-group">
          <label>
            ✓ Status:
            <select
              value={filters.selectedStatus}
              onChange={(e) => setFilters({ ...filters, selectedStatus: e.target.value })}
            >
              <option value="ALL">All Statuses</option>
              {getUniqueStatuses().map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
        </div>

        <button
          className="btn-reset-filters"
          onClick={() =>
            setFilters({
              startDate: "",
              endDate: "",
              userSearch: "",
              selectedCategory: "ALL",
              selectedStatus: "ALL",
            })
          }
        >
          ⟲ Reset Filters
        </button>
      </div>

      {/* Content */}
      <div className="accounting-content">
        {loading ? (
          <div className="spinner"></div>
        ) : (
          <>
            {/* All Transactions Tab */}
            {activeTab === "transactions" && (
              <div className="section">
                <h2>
                  Transaction History
                  {filters.startDate || filters.endDate || filters.userSearch || filters.selectedCategory !== "ALL" || filters.selectedStatus !== "ALL" ? (
                    <span className="filter-badge">(Filtered: {filteredTransactions.length} of {transactions.length})</span>
                  ) : null}
                </h2>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Type</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.slice(0, 100).map((tx) => (
                        <tr key={tx.txId}>
                          <td className="date">{formatDate(tx.createdAt)}</td>
                          <td className="user">{tx.userId.substring(0, 8)}</td>
                          <td className="type">{tx.type}</td>
                          <td className="category">{tx.category}</td>
                          <td className={`amount ${tx.amount >= 0 ? "positive" : "negative"}`}>
                            {formatCurrency(tx.amount)}
                          </td>
                          <td className={`status ${tx.status.toLowerCase()}`}>{tx.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredTransactions.length === 0 && (
                    <div className="no-data">No transactions found matching filters</div>
                  )}
                </div>
              </div>
            )}

            {/* User Balances Tab */}
            {activeTab === "users" && (
              <div className="section">
                <h2>
                  User Account Balances
                  {filters.userSearch ? (
                    <span className="filter-badge">(Filtered: {filteredUsers.length} of {userBalances.length})</span>
                  ) : null}
                </h2>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Display Name</th>
                        <th>Email</th>
                        <th>Balance</th>
                        <th>Transactions</th>
                        <th>Avg Per Transaction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.userId}>
                          <td className="name">{user.displayName}</td>
                          <td className="email">{user.email}</td>
                          <td className={`amount ${user.balance >= 0 ? "positive" : "negative"}`}>
                            {formatCurrency(user.balance)}
                          </td>
                          <td className="count">{user.transactionCount}</td>
                          <td className="amount">
                            {formatCurrency(user.transactionCount > 0 ? user.balance / user.transactionCount : 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && (
                    <div className="no-data">No users found matching filters</div>
                  )}
                </div>
              </div>
            )}

            {/* Category Totals Tab */}
            {activeTab === "categories" && (
              <div className="section">
                <h2>Transactions by Category</h2>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Transaction Count</th>
                        <th>Total Amount</th>
                        <th>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryTotals.map((cat) => (
                        <tr key={cat.category}>
                          <td className="category">{cat.category}</td>
                          <td className="count">{cat.count}</td>
                          <td className="amount">{formatCurrency(cat.total)}</td>
                          <td className="percentage">
                            {totalAmount > 0
                              ? ((cat.total / totalAmount) * 100).toFixed(1)
                              : "0"}
                            %
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="overview-section">
                <div className="overview-grid">
                  <div className="overview-box">
                    <h3>Top Users by Balance</h3>
                    <ul className="top-list">
                      {userBalances.slice(0, 5).map((user, idx) => (
                        <li key={user.userId}>
                          <span className="rank">{idx + 1}</span>
                          <span className="name">{user.displayName}</span>
                          <span className="amount">{formatCurrency(user.balance)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="overview-box">
                    <h3>Top Categories</h3>
                    <ul className="top-list">
                      {categoryTotals.slice(0, 5).map((cat, idx) => (
                        <li key={cat.category}>
                          <span className="rank">{idx + 1}</span>
                          <span className="name">{cat.category}</span>
                          <span className="amount">{formatCurrency(cat.total)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <button
        className="btn-refresh"
        onClick={() => {
          if (liveUpdates) {
            loadAccountingDataWithListener();
          } else {
            loadAccountingData();
          }
        }}
        disabled={loading}
      >
        🔄 Refresh Data
      </button>

      {liveUpdates && (
        <div className="live-indicator">
          <span className="pulse"></span> Live Updates Enabled
        </div>
      )}
    </div>
  );
}
